import { Card, CardContent } from"@shared/ui/card";
import { RadioGroup, RadioGroupItem } from"@shared/ui/radio-group";
import { AlertTriangle, CircleDot, CheckCircle2 } from"lucide-react";
import { useTranslation } from "react-i18next";

type TenantAccountCardProps = {
 showAlert: boolean;
 owner: {
 name: string;
 number: string;
 phone: string;
 address: string;
 };
 selectedOption?:"Owner"|"New"|"Master";
 onOptionChange?: (val:"Owner"|"New"|"Master") => void;
};

export const TenantAccountCard = ({
 showAlert,
 owner,
 selectedOption="Owner",
 onOptionChange,
}: TenantAccountCardProps) => {
 const { t } = useTranslation();
 const isTransferred = showAlert;
 const ownerDetails = [
 { key:"name", label: t("Owner Name")},
 { key:"phone", label: t("Owner Phone")},
 { key:"address", label: t("Owner Address")},
 ] as const;

 return (
 <Card className="rounded-xl border border-orange-100 bg-orange-50/40 overflow-hidden shadow-sm">
  <CardContent className="p-0">
  {/* Header */}
  <div className="flex items-center justify-between bg-orange-50 px-5 py-4">
   <div className="flex items-center gap-3">
   <div className="bg-orange-500 p-2.5 rounded-full flex items-center justify-center">
    <AlertTriangle className="w-4 h-4 text-white"/>
   </div>

   <div>
    <p className="font-semibold text-orange-700">
    {t("This is a Tenant Account")}
    </p>
    <p className="text-sm text-orange-600">
    {isTransferred
     ? t("Previously transferred to the property owner")
     : t("Currently linked under an owner account")}
    </p>
   </div>
   </div>

   <span className="text-xs font-semibold tracking-wider text-white bg-orange-500 px-3 py-1 rounded-full">
   {t("Tenant")}
   </span>
  </div>

  {/* Content */}
  <div className="p-5 space-y-4">
   {/* Banner */}
   <div
   className={`rounded-md px-4 py-3 text-sm ${
    isTransferred
    ?"bg-orange-50 text-orange-700 border-l-4 border-orange-400"
    :"bg-orange-50 text-orange-700 border-l-4 border-orange-400"
   }`}
   >
   {isTransferred
    ? t("This account was previously transferred to the property owner. Below are the owner's details who currently holds the account.")
    : t("This account is registered as a tenant under a property owner. Please choose whether to transfer the account back to the owner or to a new tenant.")}
   </div>

   {/* Owner Details */}
   <div className="grid grid-cols-3 gap-x-6 gap-y-4 text-sm">
   {ownerDetails.map(({ key, label }) => (
    <div key={key}>
    <p className="text-gray-500 text-xs font-semibold tracking-wider">
     {label}
    </p>
    <p className="font-semibold text-gray-900 mt-1">
     {owner[key]}
    </p>
    </div>
   ))}
   </div>

   {/* Conditional Section */}
   {isTransferred ? (
   <div className="flex items-center gap-2 text-green-700 font-medium">
    <CheckCircle2 className="w-5 h-5"/>
    {t("Transferred to Owner")}
   </div>
   ) : (
   <div>
    <p className="font-medium mb-3 flex items-center gap-2 text-gray-800">
    <CircleDot className="w-4 h-4 text-orange-500"/>
    {t("How would you like to proceed with this transfer?")}
    </p>

    <RadioGroup
    value={selectedOption}
    onValueChange={onOptionChange}
    className="grid grid-cols-2 gap-4"
    >
    {/* Option 1 */}
    <label
     className={`border rounded-lg p-4 flex gap-3 cursor-pointer transition-colors ${
     selectedOption ==="Owner"
      ?"border-blue-500 bg-blue-50 ring-1 ring-blue-500"
      :"border-gray-200 bg-white hover:border-gray-300"
     }`}
    >
     <RadioGroupItem value="Owner"className="mt-0.5"/>
     <div>
     <p className="font-semibold text-gray-900">
      {t("Transfer to Owner")}
     </p>
     <p className="text-sm text-gray-500 mt-1 leading-relaxed">
      {t("Transfer this account back to the property owner. The owner will assume all service responsibilities and billing.")}
     </p>
     </div>
    </label>

    {/* Option 2 */}
    <label
     className={`border rounded-lg p-4 flex gap-3 cursor-pointer transition-colors ${
     selectedOption ==="New"
      ?"border-blue-500 bg-blue-50 ring-1 ring-blue-500"
      :"border-gray-200 bg-white hover:border-gray-300"
     }`}
    >
     <RadioGroupItem value="New"className="mt-0.5"/>
     <div>
     <p className="font-semibold text-gray-900">
      {t("Transfer to New Tenant")}
     </p>
     <p className="text-sm text-gray-500 mt-1 leading-relaxed">
      {t("Transfer this account to a new tenant. A new tenant will take over the service at this premises.")}
     </p>
     </div>
    </label>
    </RadioGroup>
   </div>
   )}
  </div>
  </CardContent>
 </Card>
 );
};